import classNames from 'classnames';
import React, { MouseEvent, PropsWithChildren, useEffect, useRef, useState } from 'react';

import { Button, ButtonSize, ButtonVariant } from '../Buttons';
import { FocusTrap } from '../FocusTrap';
import { ScrollLock } from '../ScrollLock';
import { Swipeable, SwipeDirection } from '../Swipeable';
import { inertHelper } from '../utils/InertHelper';
import { isMobile } from '../utils/IsMobile';
import { isServerRendering } from '../utils/IsServerRendering';
import { ModalTheme } from './ModalTheme';
import { ModalVariant } from './ModalVariant';

type IModalBaseProps = PropsWithChildren<{
    variant?: ModalVariant;
    theme?: ModalTheme;
    className?: string;
    contentClassName?: string;
    shorterSwipeableOverlay?: boolean;
    hideCloseButton?: boolean;
    heading: string;
    closeLabel: string;
    errorMessage?: string;
    isVisible: boolean;
    onClose: () => void;
    onAnimationEnd?: () => void;
}>;

type IModalWithButtonsProps = IModalBaseProps & {
    onConfirm: () => void;
    confirmLabel: string;
    showConfirmLoader: boolean;
};

type IModalProps = IModalBaseProps | IModalWithButtonsProps;

const hasButtons = (props: IModalProps): props is IModalWithButtonsProps =>
    'confirmLabel' in props && !!props.confirmLabel && 'onConfirm' in props && !!props.onConfirm;

export const Modal = (props: IModalProps) => {
    const {
        variant,
        theme,
        className,
        contentClassName,
        shorterSwipeableOverlay,
        hideCloseButton,
        heading,
        closeLabel,
        errorMessage,
        isVisible,
        onClose,
        onAnimationEnd,
        children
    } = props;
    const [scrollLockActive, setScrollLockActive] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const contentTouchStartScrollTopRef = useRef<number>(0);
    const wasShownRef = useRef<boolean>(false);

    useEffect(() => {
        handleScrollLock(isVisible);

        if (isVisible) {
            wasShownRef.current = true;
        }

        if (!isServerRendering()) {
            setOverscollBehaviour(document, isVisible ? 'none' : '');

            return () => {
                setOverscollBehaviour(document, '');
            };
        }
    }, [isVisible]);

    const setOverscollBehaviour = (document: Document, value: string) => {
        document.documentElement.style.overscrollBehavior = value;
        document.body.style.overscrollBehavior = value;
    };

    const handleScrollLock = (isModalVisible: boolean) => {
        isModalVisible ? setScrollLockActive(true) : setTimeout(() => setScrollLockActive(false), 300);
    };

    const handleTouch = () => {
        contentTouchStartScrollTopRef.current = contentRef.current.scrollTop;
    };

    const handleClose = () => {
        onClose();
    };

    const handleSwipe = (swipeDirection: SwipeDirection) => {
        if (isMobile()) {
            if (swipeDirection === SwipeDirection.Bottom && contentTouchStartScrollTopRef.current == contentRef.current.scrollTop) {
                handleClose();
            }
        } else if (
            (variant === ModalVariant.Left && swipeDirection === SwipeDirection.Left) ||
            (variant === ModalVariant.Right && swipeDirection === SwipeDirection.Right)
        ) {
            handleClose();
        }
    };

    const handleAnimationEnd = () => {
        if (onAnimationEnd) {
            onAnimationEnd();
        }
        contentRef.current.scrollTo({ top: 0, left: 0 });
    };

    const modalVariantCssClass = (): string => {
        switch (variant) {
            case ModalVariant.Left:
                return 'NewModal__Inner--Left';
            case ModalVariant.Right:
                return 'NewModal__Inner--Right';
            case ModalVariant.Center:
                return 'NewModal__Inner--Center';
            default:
                return 'NewModal__Inner--NoVariant';
        }
    };

    const modalThemeCssClass = (): string => {
        switch (theme) {
            case ModalTheme.LightBlue:
                return 'NewModal__Inner--LightBlue';
            default:
                return 'NewModal__Inner--Default';
        }
    };

    const handleClickOutside = (event: MouseEvent<HTMLElement>) => {
        const modal = modalRef.current;

        if (modal && !modal.contains(event.target as Node)) {
            handleClose();
        }
    };

    return (
        <Swipeable onTouch={handleTouch} onSwipe={handleSwipe} waitForEnd>
            <FocusTrap enabled={isVisible}>
                <aside
                    className={classNames('NewModal', { 'NewModal--Hidden': !isVisible, 'NewModal--WasShown': wasShownRef.current })}
                    onClick={handleClickOutside}
                    onAnimationEnd={handleAnimationEnd}
                    {...inertHelper(isVisible)}
                >
                    <ScrollLock isLocked={isVisible || scrollLockActive} />
                    <div
                        ref={modalRef}
                        className={classNames('NewModal__Inner', className, modalVariantCssClass(), modalThemeCssClass())}
                        role="dialog"
                        aria-modal
                        aria-hidden={!isVisible}
                    >
                        <div className="NewModal__DragHandle" />
                        {!hideCloseButton && (
                            <button className="NewModal__CloseButton" onClick={handleClose}>
                                <span className="visuallyHidden">{closeLabel}</span>
                            </button>
                        )}
                        {heading && <h2 className="NewModal__Heading">{heading}</h2>}
                        <div
                            className={classNames('NewModal__SwipeableOverlay', {
                                NewModal__ShorterSwipeableOverlay: shorterSwipeableOverlay
                            })}
                        />
                        <div className={classNames('NewModal__Content', contentClassName)} ref={contentRef}>
                            {children}
                        </div>
                        {hasButtons(props) && (
                            <div className="NewModal__Buttons">
                                <Button size={ButtonSize.Medium} variant={ButtonVariant.Link} onClick={handleClose}>
                                    {closeLabel}
                                </Button>
                                <Button size={ButtonSize.Medium} variant={ButtonVariant.Primary} onClick={props.onConfirm} loading={props.showConfirmLoader}>
                                    {props.confirmLabel}
                                </Button>
                            </div>
                        )}
                        {errorMessage && <div className="NewModal__Error">{errorMessage}</div>}
                    </div>
                </aside>
            </FocusTrap>
        </Swipeable>
    );
};
